import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { collection, onSnapshot, addDoc, deleteDoc, doc, query, where, orderBy, getCountFromServer, getDoc, updateDoc, getDocs, writeBatch, serverTimestamp } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Copy, Shuffle, Skull, Trophy, Medal, Bell, Trash, PencilSimple, Play, X, QrCode } from '@phosphor-icons/react';
import { QRCodeSVG } from 'qrcode.react';

function TeacherDashboard() {
  const [quizzes, setQuizzes] = useState([]);
  const [userId, setUserId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newQuizName, setNewQuizName] = useState('');
  const [quizLink, setQuizLink] = useState('');
  const [modalType, setModalType] = useState('create'); // 'create' or 'play'
  const [questionCounts, setQuestionCounts] = useState({});
  const [isRandomOrder, setIsRandomOrder] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedQuizId, setSelectedQuizId] = useState(null);
  const [isExtremeMode, setIsExtremeMode] = useState(false);
  const [newLeaderboardEntries, setNewLeaderboardEntries] = useState([]);
  const [leaderboardListener, setLeaderboardListener] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribeAuth();
  }, [navigate]);

  useEffect(() => {
    let unsubscribeQuizzes;

    if (userId) {
      const q = query(
        collection(db, 'quizzes'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      unsubscribeQuizzes = onSnapshot(q, (snapshot) => {
        const updatedQuizzes = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setQuizzes(updatedQuizzes);

        updatedQuizzes.forEach(quiz => {
          getQuestionCount(quiz.id);
        });
      });
    }

    return () => {
      if (unsubscribeQuizzes) {
        unsubscribeQuizzes();
      }
    };
  }, [userId]);

  const getQuestionCount = async (quizId) => {
    const questionsRef = collection(db, `quizzes/${quizId}/questions`);
    const snapshot = await getCountFromServer(questionsRef);
    setQuestionCounts(prev => ({ ...prev, [quizId]: snapshot.data().count }));
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setQuizLink('');
    setNewQuizName('');
    setModalType('create');
  };

  const createNewQuiz = async (e) => {
    e.preventDefault();
    if (!newQuizName.trim()) return;

    try {
      const user = auth.currentUser;
      if (!user) {
        console.error("No authenticated user");
        return;
      }

      const code = generateUniqueCode();
      const quizRef = await addDoc(collection(db, 'quizzes'), {
        name: newQuizName,
        userId: user.uid,
        createdAt: serverTimestamp(),
        code: code,
        isRandomOrder: false,
        isExtremeMode: false
      });

      closeModal();
      navigate(`/quiz-dashboard/${quizRef.id}`);
    } catch (error) {
      console.error("Error creating quiz: ", error);
    }
  };

  const generateUniqueCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const deleteQuiz = async (quizId) => {
    if (window.confirm('정말로 이 퀴즈를 삭제하시겠습니까?')) {
      await deleteDoc(doc(db, 'quizzes', quizId));
    }
  };

  const navigateToQuizEdit = (quizId) => {
    navigate(`/quiz-dashboard/${quizId}`);
  };

  const openPlayModal = (quizId) => {
    const quiz = quizzes.find(q => q.id === quizId);
    if (quiz) {
      setIsRandomOrder(quiz.isRandomOrder || false);
      setIsExtremeMode(quiz.isExtremeMode || false);
      setQuizLink(`${window.location.origin}/quiz-session/${quizId}`);
      setModalType('play');
      setIsModalOpen(true);
    }
  };

  const toggleRandomOrder = async (quizId) => {
    const quizRef = doc(db, 'quizzes', quizId);
    const quizDoc = await getDoc(quizRef);
    if (quizDoc.exists()) {
      const currentIsRandomOrder = quizDoc.data().isRandomOrder || false;
      await updateDoc(quizRef, { isRandomOrder: !currentIsRandomOrder });
      setIsRandomOrder(!currentIsRandomOrder);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('링크가 클립보드에 복사되었습니다.');
    } catch (err) {
      console.error('클립보드 복사 실패:', err);
      alert('링크 복사에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const openLeaderboard = useCallback((quizId) => {
    setSelectedQuizId(quizId);
    setShowLeaderboard(true);
    setNewLeaderboardEntries([]);

    if (leaderboardListener) {
      leaderboardListener();
    }

    const q = query(
      collection(db, `quizzes/${quizId}/results`),
      orderBy("score", "desc"),
      orderBy("timestamp", "asc")
    );

    let isInitialLoad = true;

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const leaderboardData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLeaderboard(leaderboardData);

      if (!isInitialLoad) {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            setNewLeaderboardEntries(prev => [...prev, change.doc.id]);
            setTimeout(() => {
              setNewLeaderboardEntries(prev => prev.filter(id => id !== change.doc.id));
            }, 5000);
          }
        });
      } else {
        isInitialLoad = false;
      }
    }, (error) => {
      console.error("Error fetching leaderboard:", error);
    });

    setLeaderboardListener(() => unsubscribe);
  }, [leaderboardListener]);

  const resetLeaderboard = async () => {
    if (window.confirm("정말로 리더보드를 초기화하시겠습니까?")) {
      const q = query(collection(db, `quizzes/${selectedQuizId}/results`));
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);
      querySnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      setLeaderboard([]);
    }
  };

  const toggleExtremeMode = async (quizId) => {
    const quizRef = doc(db, 'quizzes', quizId);
    const quizDoc = await getDoc(quizRef);
    if (quizDoc.exists()) {
      const currentIsExtremeMode = quizDoc.data().isExtremeMode || false;
      await updateDoc(quizRef, { isExtremeMode: !currentIsExtremeMode });
      setIsExtremeMode(!currentIsExtremeMode);
    }
  };

  useEffect(() => {
    return () => {
      if (leaderboardListener) {
        leaderboardListener();
      }
    };
  }, [leaderboardListener]);

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-green-100 via-pink-50 to-green-50 pt-32 pb-40 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800">내 퀴즈 관리</h2>
          <Button onClick={() => setIsModalOpen(true)} className="gap-2 bg-green-400 hover:bg-green-500 shadow-lg shadow-green-400/20 hover:shadow-green-400/30 transition-all h-11 px-6 border-0">
            <Plus size={20} weight="bold" />
            <span>퀴즈 생성</span>
          </Button>
        </div>

        {quizzes.length > 0 ? (
          <div className="grid grid-cols-1 gap-3">
            {quizzes.map((quiz) => (
              <Card key={quiz.id} className="group hover:shadow-lg transition-all duration-300 border-0 bg-white overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    {/* Left Section - Quiz Info */}
                    <div className="flex items-center gap-3 flex-grow min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 transition-colors">
                        <Trophy size={20} weight="duotone" className="text-green-500" />
                      </div>
                      <div className="flex-grow min-w-0">
                        <Link to={`/quiz-dashboard/${quiz.id}`} className="text-lg font-bold text-gray-800 hover:text-green-500 transition-colors block truncate">
                          {quiz.name}
                        </Link>
                        <p className="text-xs text-gray-500">
                          총 {questionCounts[quiz.id] || 0}개 문제
                        </p>
                      </div>
                    </div>

                    {/* Right Section - Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        onClick={() => openPlayModal(quiz.id)}
                        size="sm"
                        className="h-8 px-3 bg-green-400 hover:bg-green-500 text-white shadow-sm hover:shadow-md transition-all border-0"
                      >
                        <Play size={14} weight="fill" className="mr-1" /> PLAY
                      </Button>
                      <Button
                        onClick={() => openLeaderboard(quiz.id)}
                        size="sm"
                        variant="outline"
                        className="h-8 px-3 border-pink-200 text-pink-600 hover:bg-pink-50 hover:text-pink-700 hover:border-pink-300 transition-all"
                      >
                        <Trophy size={14} weight="fill" className="mr-1" /> 순위
                      </Button>
                      <Button
                        onClick={() => navigateToQuizEdit(quiz.id)}
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-gray-600 hover:text-green-500 hover:bg-green-50"
                      >
                        <PencilSimple size={16} weight="bold" />
                      </Button>
                      <Button
                        onClick={() => deleteQuiz(quiz.id)}
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-gray-600 hover:text-red-500 hover:bg-red-50"
                      >
                        <Trash size={16} weight="bold" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-24 bg-white/60 backdrop-blur-sm rounded-3xl border-2 border-dashed border-gray-200">
            <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <Plus size={32} weight="bold" className="text-gray-400" />
            </div>
            <p className="text-gray-500 text-lg mb-4">퀴즈가 없습니다. 새 퀴즈를 만들어보세요!</p>
            <Button onClick={() => setIsModalOpen(true)} className="bg-green-400 hover:bg-green-500 shadow-lg shadow-green-400/20 hover:shadow-green-400/30 transition-all border-0">
              <Plus size={18} weight="bold" className="mr-2" />
              지금 만들기
            </Button>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md shadow-2xl animate-in fade-in zoom-in duration-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xl">
                {modalType === 'create' ? '새 퀴즈 생성' : '퀴즈 시작'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={closeModal} className="h-8 w-8 rounded-full">
                <X size={20} />
              </Button>
            </CardHeader>
            <CardContent>
              {modalType === 'create' ? (
                <form onSubmit={createNewQuiz} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="quizName">퀴즈 이름</Label>
                    <Input
                      id="quizName"
                      type="text"
                      value={newQuizName}
                      onChange={(e) => setNewQuizName(e.target.value)}
                      placeholder="예: 1학기 중간고사 대비"
                      required
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={closeModal}>취소</Button>
                    <Button type="submit">생성</Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-4 rounded-xl text-center shadow-lg">
                    <p className="font-semibold">링크 또는 QR코드로 퀴즈를 시작하세요!</p>
                  </div>

                  {/* QR 코드 섹션 */}
                  <div
                    className="flex flex-col items-center bg-white border-2 border-dashed border-gray-200 rounded-xl p-6 cursor-pointer hover:border-gray-300 hover:bg-gray-50 transition-all"
                    onClick={() => setShowQRModal(true)}
                    title="클릭하여 확대"
                  >
                    <div className="bg-white rounded-lg">
                      <QRCodeSVG
                        value={quizLink}
                        size={160}
                        level="H"
                        includeMargin={true}
                        bgColor="#ffffff"
                        fgColor="#000000"
                      />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                      <QrCode size={16} weight="duotone" />
                      <span>클릭하여 크게 보기</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>퀴즈 링크</Label>
                    <div className="flex gap-2">
                      <Input value={quizLink} readOnly className="bg-gray-50 text-sm" />
                      <Button onClick={() => copyToClipboard(quizLink)} variant="outline" size="icon" title="링크 복사">
                        <Copy size={18} />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Shuffle size={18} />
                        <span>랜덤 순서</span>
                      </div>
                      <div
                        onClick={() => toggleRandomOrder(quizzes.find(q => q.id === quizLink.split('/').pop()).id)}
                        className={`w-12 h-6 rounded-full p-1 cursor-pointer transition-colors ${isRandomOrder ? 'bg-primary' : 'bg-gray-300'}`}
                      >
                        <div className={`w-4 h-4 rounded-full bg-white transition-transform ${isRandomOrder ? 'translate-x-6' : 'translate-x-0'}`} />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Skull size={18} />
                        <span>익스트림 모드 (정답 미공개)</span>
                      </div>
                      <div
                        onClick={() => toggleExtremeMode(quizzes.find(q => q.id === quizLink.split('/').pop()).id)}
                        className={`w-12 h-6 rounded-full p-1 cursor-pointer transition-colors ${isExtremeMode ? 'bg-primary' : 'bg-gray-300'}`}
                      >
                        <div className={`w-4 h-4 rounded-full bg-white transition-transform ${isExtremeMode ? 'translate-x-6' : 'translate-x-0'}`} />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Button onClick={() => window.open(quizLink, '_blank')} className="w-full">
                      퀴즈 시작
                    </Button>
                    <Button onClick={closeModal} variant="outline" className="w-full">
                      닫기
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {showLeaderboard && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="text-yellow-500" weight="fill" />
                  리더보드
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowLeaderboard(false)}>
                  <X size={20} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-grow overflow-y-auto p-0">
              {leaderboard.length > 0 ? (
                <ul className="divide-y divide-gray-100">
                  {leaderboard.map((entry, index) => {
                    let icon = null;
                    if (index === 0) icon = <Trophy size={24} weight="fill" className="text-yellow-400" />;
                    else if (index === 1) icon = <Medal size={24} weight="fill" className="text-gray-400" />;
                    else if (index === 2) icon = <Medal size={24} weight="fill" className="text-orange-400" />;
                    else icon = <span className="w-6 text-center font-bold text-gray-400">{index + 1}</span>;

                    return (
                      <li key={entry.id} className={`flex justify-between items-center p-4 hover:bg-gray-50 transition-colors ${newLeaderboardEntries.includes(entry.id) ? 'bg-yellow-50 animate-pulse' : ''}`}>
                        <div className="flex items-center gap-4">
                          <div className="w-8 flex justify-center">{icon}</div>
                          <div className="flex items-center gap-3">
                            <img
                              src={`https://api.dicebear.com/9.x/notionists/svg?seed=${entry.id}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf`}
                              alt={entry.nickname}
                              className="w-10 h-10 rounded-full shadow-sm border border-gray-100 bg-white"
                            />
                            <span className="font-semibold text-lg text-gray-800">{entry.nickname}</span>
                          </div>
                          {newLeaderboardEntries.includes(entry.id) && <Bell weight="fill" className="text-blue-500 animate-bounce" />}
                        </div>
                        <span className="font-bold text-primary text-lg">{entry.score} / {entry.totalQuestions}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                  <Trophy size={48} weight="duotone" className="mb-4 opacity-50" />
                  <p>아직 참여 기록이 없습니다.</p>
                </div>
              )}
            </CardContent>
            <CardFooter className="border-t bg-gray-50 p-4 flex justify-between">
              <Button variant="destructive" size="sm" onClick={resetLeaderboard}>
                초기화
              </Button>
              <Button onClick={() => setShowLeaderboard(false)}>
                닫기
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      {/* QR 확대 모달 */}
      {showQRModal && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => setShowQRModal(false)}
        >
          <div
            className="bg-white rounded-3xl p-8 shadow-2xl animate-in fade-in zoom-in duration-200 flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white p-4 rounded-2xl mb-4">
              <QRCodeSVG
                value={quizLink}
                size={320}
                level="H"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </div>
            <p className="text-gray-600 text-center mb-4">스마트폰으로 QR코드를 스캔하세요</p>
            <Button
              onClick={() => setShowQRModal(false)}
              variant="outline"
              className="px-8"
            >
              닫기
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherDashboard;
